import type { PartialParking } from '@repo/domain';
import { ParkingEntranceMarkerIcon } from '@repo/entities';
import type { MapArea, MapPin } from '@repo/map';

import { PARKING_SHAPE_COLOR, PARKING_SHAPE_SELECTED_COLOR } from '@features/parkingMap/constants/parkingShapes';

// The entrance of the selected parking is drawn above the others.
const ENTRANCE_PIN_Z_INDEX = 1;
const SELECTED_ENTRANCE_PIN_Z_INDEX = 2;

export interface ParkingShapes {
  areas: MapArea[];
  pins: MapPin[];
  /** The slug of the parking each area and pin belongs to, by its key. */
  parkingSlugByKey: Map<string, string>;
}

/** The borders (clickable areas) and the entrance markers of the parkings, in brand-2 for the selected one. */
export const createParkingShapes = (parkings: PartialParking[], selectedSlug: string | undefined): ParkingShapes => {
  const shapes: ParkingShapes = { areas: [], pins: [], parkingSlugByKey: new Map<string, string>() };

  for (const parking of parkings) {
    const isSelected = parking.slug === selectedSlug;

    parking.paths.forEach((points, pathPosition) => {
      const key = `${parking.id}:path:${pathPosition}`;
      shapes.areas.push({
        key,
        points,
        clickable: true,
        color: isSelected ? PARKING_SHAPE_SELECTED_COLOR : PARKING_SHAPE_COLOR,
      });
      shapes.parkingSlugByKey.set(key, parking.slug);
    });

    if (parking.entrance) {
      const key = `${parking.id}:entrance`;
      shapes.pins.push({
        key,
        position: parking.entrance,
        content: <ParkingEntranceMarkerIcon isSelected={isSelected} />,
        anchor: 'center',
        zIndex: isSelected ? SELECTED_ENTRANCE_PIN_Z_INDEX : ENTRANCE_PIN_Z_INDEX,
      });
      shapes.parkingSlugByKey.set(key, parking.slug);
    }
  }

  return shapes;
};
